import React from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from './ui/table';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const rateLabel = (payType, payRate, load) => {
  if (load.pay_method === 'fixed') return 'fixed';
  if (load.pay_method === 'percentage') return `${Number(load.pay_method_rate || 0)}%`;
  const r = Number(payRate || 0);
  if (payType === 'per_mile') return `${Number(load.miles || 0).toLocaleString()} mi × $${r}`;
  if (payType === 'per_load_flat') return 'flat';
  return `${r}%`;
};

/** Per-load driver pay breakdown. `showGross` reveals load gross + rate columns. */
export const PaystubLoadsTable = ({ loads = [], payType, payRate, showGross, onToggle }) => {
  const loadBased = loads.some((l) => l.driver_pay !== null && l.driver_pay !== undefined);
  const active = loads.filter((l) => !l.excluded_from_paystub);
  const totalGross = active.reduce((s, l) => s + Number(l.payout_amount || 0), 0);
  const totalPay = active.reduce((s, l) => s + Number(l.driver_pay || 0), 0);
  const lead = (onToggle ? 1 : 0) + 3;

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[640px]" data-testid="paystub-loads-table">
        <TableHeader>
          <TableRow>
            {onToggle && <TableHead className="w-[70px] text-center">Include</TableHead>}
            <TableHead>Load ID</TableHead>
            <TableHead>Broker</TableHead>
            <TableHead>Date</TableHead>
            {showGross && <TableHead className="text-right" data-testid="paystub-col-load-gross">Load Gross</TableHead>}
            {showGross && loadBased && <TableHead className="text-right">Rate</TableHead>}
            {loadBased && <TableHead className="text-right" data-testid="paystub-col-driver-pay">Driver Pay</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loads.map((l) => (
            <TableRow key={l.id} className={l.excluded_from_paystub ? 'opacity-45' : ''} data-testid={`paystub-load-row-${l.id}`}>
              {onToggle && (
                <TableCell className="text-center">
                  <input
                    type="checkbox"
                    checked={!l.excluded_from_paystub}
                    onChange={(e) => onToggle(l.id, !e.target.checked)}
                    className="accent-blue-600 cursor-pointer"
                    data-testid={`paystub-load-toggle-${l.id}`}
                  />
                </TableCell>
              )}
              <TableCell className="font-mono text-xs">{l.load_reference || l.id?.slice(0, 6)}</TableCell>
              <TableCell className="text-xs">{l.broker_name || '—'}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{l.start_date || l.week_start || '—'}</TableCell>
              {showGross && <TableCell className="text-right font-mono text-xs" data-testid={`paystub-load-gross-${l.id}`}>{money(l.payout_amount)}</TableCell>}
              {showGross && loadBased && <TableCell className="text-right font-mono text-xs text-slate-500">× {rateLabel(payType, payRate, l)}</TableCell>}
              {loadBased && (
                <TableCell className="text-right font-mono text-xs font-semibold text-emerald-700" data-testid={`paystub-load-driver-pay-${l.id}`}>
                  {money(l.driver_pay)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
        {(showGross || loadBased) && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={lead} className="text-xs font-semibold">Total (included loads)</TableCell>
              {showGross && <TableCell className="text-right font-mono text-xs font-semibold" data-testid="paystub-total-load-gross">{money(totalGross)}</TableCell>}
              {showGross && loadBased && <TableCell />}
              {loadBased && <TableCell className="text-right font-mono text-xs font-bold text-emerald-700" data-testid="paystub-total-driver-pay">{money(totalPay)}</TableCell>}
            </TableRow>
          </TableFooter>
        )}
      </Table>
      {!loadBased && (
        <p className="text-[11px] text-muted-foreground px-4 py-2">
          This driver's pay isn't calculated per load, so only the period total is shown above.
        </p>
      )}
    </div>
  );
};
